import { useState } from "react";
import { useTranslation } from "react-i18next";
import api from "../../../api";
import { Receipt, HttpError } from "../../../api/types";
import useShowToast from "../../../hooks/useShowToast";

/**
 * Custom hook to fetch the management receipts list from backend.
 * @param {Date} dateFrom - Start date
 * @param {Date} dateTo - End date
 * @returns {object} - Contains receipts data, loading state, error state, and refetch function.
 */
function useFetchManagementReceipts() {
  const { t } = useTranslation();
  const [receipts, setReceipts] = useState<Receipt[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<HttpError | null>(null);
  const { errorToast } = useShowToast();

  const fetchReceipts = async (dateFrom: Date, dateTo: Date) => {
    if (!dateFrom || !dateTo) {
      setReceipts([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const receiptsList = await api.getManagementReceipts(dateFrom, dateTo);

      if (api.error) {
        setError(api.error);
        errorToast({
          title: t("club_management.receipts.fetch.error_title"),
          description: t("club_management.receipts.fetch.error_description"),
        });
      }

      setReceipts(receiptsList || []);
    } finally {
      setLoading(false);
    }
  };

  return {
    receipts,
    loading,
    error,
    refetchReceipts: fetchReceipts,
  };
}

export default useFetchManagementReceipts;
